import {
  collectTemplateRefs,
  jsonPointerGet,
  parseToolCallParamBindingRef,
  type ParsedToolCallParamBindingRef,
} from '@cotera/utilities';
import type { ToolCallUpstreamSample } from '../../../hooks/use-tool-call-upstream-samples';
import { displayDottedPathFromRef } from './variable-picker-data';

/**
 * A ref this node holds that reads nothing.
 *
 * The client half of the server's param-binding validation, run against the
 * same upstream samples the `/` picker offers. Reported rather than silently
 * tolerated because a ref that resolves to nothing is not a value the tool can
 * be given: the write goes out with the column blank, or the row addressed by
 * an empty id, and the first sign of it is a run that did the wrong thing hours
 * later.
 */
export type ToolInputRefIssue = {
  /** The top-level param the ref sits under, which is where the message goes. */
  readonly field: string;
  /**
   * The exact string holding it, as a dotted path — `rows.0.values.email` for a
   * dataset write's cell, or just the param name for a whole-value binding.
   */
  readonly path: string;
  readonly ref: string;
  readonly message: string;
};

const parseRef = (ref: string): ParsedToolCallParamBindingRef | null => {
  try {
    return parseToolCallParamBindingRef(ref);
  } catch {
    return null;
  }
};

/**
 * Whether this source's sample says anything about what it contains.
 *
 * A predecessor whose output shape cannot be derived is offered as a single
 * whole-output option with a placeholder string for a sample (see
 * `buildToolCallUpstreamSamples`). Checking a pointer against that would report
 * every field of an untyped agent's output as missing, so an unknown shape is
 * accepted — the same call the server makes when it has no sample for a source.
 */
const describesItsShape = (sample: unknown): boolean =>
  sample !== null && typeof sample === 'object';

/**
 * Whether a JSON Pointer names at least one field — `/assignee_id` does,
 * `` (whole value) and `/` do not. A bare pointer that addresses the whole
 * previous output is a deliberate whole-value bind, not a field read, so it is
 * not checked against the shape.
 */
const hasPointerSegments = (pointer: string): boolean =>
  pointer.split('/').some((segment) => segment !== '');

/**
 * Every ref in this node's params and bindings that no longer reads anything.
 *
 * Both are checked together because both are the same ref format resolved by
 * the same code at run time; only where they are written differs. A dataset
 * write holds all of its in `params`, several structures down.
 */
export function toolInputRefIssues(args: {
  readonly params: Readonly<Record<string, unknown>>;
  readonly bindings: Readonly<Record<string, string>>;
  readonly upstreamSamples: readonly ToolCallUpstreamSample[];
}): readonly ToolInputRefIssue[] {
  // Nothing upstream is *known* yet — the graph, the tool catalog and the
  // dataset's columns all arrive asynchronously. Reporting every ref as broken
  // for that first render would flash an error on every open of a node that is
  // perfectly well wired.
  if (args.upstreamSamples.length === 0) {
    return [];
  }
  const sampleBySourceName = new Map(
    args.upstreamSamples.map((sample) => [sample.sourceName, sample] as const)
  );

  const entries: { path: string; ref: string }[] = [
    ...Object.entries(args.bindings).map(([path, ref]) => ({ path, ref })),
    ...collectTemplateRefs(args.params).map(({ path, ref }) => ({ path, ref })),
  ];

  const issues: ToolInputRefIssue[] = [];
  for (const { path, ref } of entries) {
    const field = path.split('.')[0] ?? path;
    const report = (message: string): void => {
      issues.push({ field, path, ref, message });
    };
    const parsed = parseRef(ref);
    if (parsed === null) {
      report(`"${ref}" is not a valid reference to an earlier step.`);
      continue;
    }
    // A bare JSON Pointer names no source — the legacy single-upstream form,
    // which nothing in this editor writes any more but which the run-time
    // resolver still honours by reading it off the DIRECT predecessor's output
    // (`resolveStepInputRoot`). So it is checked against that one sample, the
    // `sourceDepth === 1` step, rather than a named source. Only when there is
    // exactly one direct predecessor whose shape is known: a fan-in (several
    // direct predecessors) resolves against a merged record this sample set
    // does not model, and an unknown/opaque shape (a bucket, a group-by, an
    // untyped agent) is permissive — same as the source-qualified path below.
    // Without this a bare pointer at a field that does not exist resolved to an
    // empty string at run time with no warning, the exact silent-"" failure the
    // rest of this check exists to prevent.
    if (parsed.sourceName === undefined) {
      const directSamples = args.upstreamSamples.filter(
        (sample) => sample.sourceDepth === 1
      );
      const direct = directSamples.length === 1 ? directSamples[0] : undefined;
      if (
        direct !== undefined &&
        describesItsShape(direct.sample) &&
        hasPointerSegments(parsed.pointer) &&
        jsonPointerGet(direct.sample, parsed.pointer) === undefined
      ) {
        report(
          `${direct.label} has no ${displayDottedPathFromRef(
            ref
          )}. Pick the value again.`
        );
      }
      continue;
    }
    const source = sampleBySourceName.get(parsed.sourceName);
    if (source === undefined) {
      report(
        `${displayDottedPathFromRef(
          ref
        )} comes from a step that is no longer ` +
          'before this one. Pick the value again.'
      );
      continue;
    }
    if (!describesItsShape(source.sample)) {
      continue;
    }
    if (jsonPointerGet(source.sample, parsed.pointer) === undefined) {
      report(
        `${source.label} has no ${displayDottedPathFromRef(
          ref
        )}. Pick the value again.`
      );
    }
  }
  return issues;
}

/** The issues on one param, for a control that renders its own fields. */
export function refIssuesForField(
  issues: readonly ToolInputRefIssue[],
  field: string
): readonly ToolInputRefIssue[] {
  return issues.filter((issue) => issue.field === field);
}
